'use client'

import { cn } from '@brew/ui/cn'
import { FileCode2, Mail, Sparkles, Upload } from 'lucide-react'
import {
  type Dispatch,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useId,
} from 'react'
import { homeButtonClasses } from '@/components/marketing/home/home-button'
import {
  MK_CAPTION,
  MK_CARD,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import {
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES,
  utf8ByteLength,
} from '@/lib/free-tools/email-audit/contracts'
import {
  EMAIL_AUDIT_FORM,
  EMAIL_AUDIT_TABS,
  type EmailAuditTabId,
} from '@/lib/site/email-audit-content'
import { formatAuditBytes } from './email-audit-format'
import {
  ByEmailPanel,
  EnvelopeFields,
  PastePanel,
  UploadPanel,
} from './email-audit-input-panels'
import { EmailAuditProgress } from './email-audit-progress'
import type {
  EmailAuditToolAction,
  EmailAuditToolState,
} from './email-audit-tool-state'

const TAB_ICON = { paste: FileCode2, upload: Upload, email: Mail } as const

type InputCardProps = {
  state: EmailAuditToolState
  dispatch: Dispatch<EmailAuditToolAction>
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onSample: () => void
  isSampleLoading: boolean
}

/**
 * The free audit's input: paste, upload or send by email, then one button.
 * While the audit runs the card shows its progress in place, so the page
 * never jumps.
 */
export function EmailAuditInputCard({
  state,
  dispatch,
  onSubmit,
  onSample,
  isSampleLoading,
}: InputCardProps) {
  const baseId = useId()
  const isRunning = state.status.kind === 'running'
  const tabId = (tab: EmailAuditTabId) => `${baseId}-tab-${tab}`
  const panelId = `${baseId}-panel`

  const handleTabKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
      if (!step) {
        return
      }
      event.preventDefault()
      const index = EMAIL_AUDIT_TABS.findIndex((tab) => tab.id === state.tab)
      const next =
        EMAIL_AUDIT_TABS[
          (index + step + EMAIL_AUDIT_TABS.length) % EMAIL_AUDIT_TABS.length
        ]
      if (next) {
        dispatch({ type: 'tab', tab: next.id })
        document.getElementById(`${baseId}-tab-${next.id}`)?.focus()
      }
    },
    [baseId, dispatch, state.tab]
  )

  return (
    <form
      onSubmit={onSubmit}
      aria-busy={isRunning}
      className={cn(
        MK_CARD,
        'flex w-full max-w-3xl flex-col overflow-clip text-left'
      )}
    >
      <div className="flex items-center justify-between gap-3 border-mk-card-line border-b p-2 sm:p-3">
        <div
          role="tablist"
          aria-label="How to add your email"
          onKeyDown={handleTabKey}
          className="flex min-w-0 gap-1 rounded-full bg-mk-tile p-1 ring-1 ring-mk-line"
        >
          {EMAIL_AUDIT_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              id={tabId(tab.id)}
              panelId={panelId}
              tab={tab.id}
              label={tab.label}
              isActive={state.tab === tab.id}
              isDisabled={isRunning}
              dispatch={dispatch}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onSample}
          disabled={isRunning || isSampleLoading}
          className={cn(
            MK_CAPTION,
            'flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-mk-accent transition-colors duration-quick ease-smooth-out hover:bg-mk-tile disabled:opacity-50'
          )}
        >
          <Sparkles aria-hidden="true" className="size-3.5" />
          <span className="hidden sm:inline">{EMAIL_AUDIT_FORM.sample}</span>
          <span className="sm:hidden">{EMAIL_AUDIT_FORM.sampleShort}</span>
        </button>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(state.tab)}
        className="flex flex-col"
      >
        {isRunning ? (
          <EmailAuditProgress className="min-h-72 justify-center px-6" />
        ) : (
          <TabBody state={state} dispatch={dispatch} />
        )}
        {state.inputError ? (
          <p
            role="alert"
            className={cn(
              MK_SMALL,
              'px-5 pb-4 text-red-600 sm:px-6 dark:text-red-400'
            )}
          >
            {state.inputError}
          </p>
        ) : null}
        {state.status.kind === 'error' ? (
          <p
            role="alert"
            className={cn(
              MK_SMALL,
              'px-5 pb-4 text-red-600 sm:px-6 dark:text-red-400'
            )}
          >
            {state.status.message}
          </p>
        ) : null}
      </div>

      {state.tab === 'email' ? null : (
        <div className="flex flex-col-reverse items-stretch gap-3 border-mk-card-line border-t bg-mk-tile/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span className={cn(MK_CAPTION, 'font-mk-mono text-mk-muted')}>
            {state.html
              ? `${formatAuditBytes(utf8ByteLength(state.html))} of ${formatAuditBytes(FREE_EMAIL_AUDIT_MAX_HTML_BYTES)}`
              : EMAIL_AUDIT_FORM.sizeHint}
          </span>
          <button
            type="submit"
            disabled={isRunning}
            className={homeButtonClasses({
              className: 'h-11 gap-1 px-6 text-[15px] disabled:opacity-70',
            })}
          >
            {isRunning ? 'Auditing…' : EMAIL_AUDIT_FORM.submit}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </form>
  )
}

function TabButton({
  id,
  panelId,
  tab,
  label,
  isActive,
  isDisabled,
  dispatch,
}: {
  id: string
  panelId: string
  tab: EmailAuditTabId
  label: string
  isActive: boolean
  isDisabled: boolean
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  const Icon = TAB_ICON[tab]
  const handleClick = useCallback(
    () => dispatch({ type: 'tab', tab }),
    [dispatch, tab]
  )
  return (
    <button
      id={id}
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={isActive ? 0 : -1}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        MK_CAPTION,
        'flex h-8 min-w-0 items-center gap-1.5 rounded-full px-3 font-medium text-mk-muted transition-[color,background-color,box-shadow] duration-quick ease-smooth-out hover:text-mk-ink focus-visible:outline-2 focus-visible:outline-mk-accent disabled:opacity-60',
        { 'bg-mk-card text-mk-ink shadow-mk-card': isActive }
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  )
}

function TabBody({
  state,
  dispatch,
}: {
  state: EmailAuditToolState
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  if (state.tab === 'email') {
    return <ByEmailPanel />
  }
  return (
    <>
      {state.tab === 'paste' ? (
        <PastePanel html={state.html} dispatch={dispatch} />
      ) : (
        <UploadPanel file={state.file} dispatch={dispatch} />
      )}
      <EnvelopeFields
        subject={state.subject}
        previewText={state.previewText}
        dispatch={dispatch}
      />
    </>
  )
}
